"use strict";

var _interopRequireWildcard = require("@babel/runtime/helpers/interopRequireWildcard").default;
var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault").default;
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.Tab = exports.StyledTab = void 0;
var _extends2 = _interopRequireDefault(require("@babel/runtime/helpers/extends"));
var _styled = _interopRequireDefault(require("@emotion/styled"));
var _web = require("@react-hookz/web");
var _designTokens = require("@smartsheet/design-tokens");
var _lodestarUtils = require("@smartsheet/lodestar-utils");
var _react = _interopRequireWildcard(require("react"));
var _useTheme = require("../../theme/utils/useTheme");
var _createFocusRingStyles = require("../../util/createFocusRingStyles");
var _withFocusClassName = require("../../util/withFocusClassName");
var _TabsContext = require("./TabsContext");
var _tabsTheme = require("./tabsTheme");
const StyledTab = (0, _withFocusClassName.withFocusToggleClassName)((0, _styled.default)("button")`
    background-color: transparent;
    border: none;
    color: ${_ref => {
  let {
    themeMode,
    renderOn
  } = _ref;
  return _tabsTheme.tabsTheme[renderOn].textColor(themeMode);
}};
    transition: color ${_designTokens.motion.animation.default};
    font-size: 14px;
    font-weight: ${_ref2 => {
  let {
    themeMode
  } = _ref2;
  return _tabsTheme.tabsTheme.font.weight(themeMode);
}};
    // Override chrome's ua stylesheet which specifies buttons use Arial (wut?).
    font-family: inherit;
    line-height: ${_ref3 => {
  let {
    themeMode
  } = _ref3;
  return _tabsTheme.tabsTheme.font.lineHeight(themeMode);
}};
    margin: 0;
    padding: 0px;
    height: 100%;
    cursor: pointer;

    // handling for added icons
    display: flex;
    align-items: center;
    gap: 4px;

    // This is to ensure these tabs appear on top of the Underlines.
    position: relative;
    z-index: 2;

    :not(:first-of-type) {
        margin-left: ${_ref4 => {
  let {
    themeMode
  } = _ref4;
  return _tabsTheme.tabsTheme.tabMarginLeft(themeMode);
}};
    }

    &:active {
        color: ${_ref5 => {
  let {
    themeMode,
    renderOn
  } = _ref5;
  return _tabsTheme.tabsTheme[renderOn].activeTextColor(themeMode);
}};
    }

    ${_ref6 => {
  let {
    selected,
    themeMode
  } = _ref6;
  if (selected) {
    return {
      color: _tabsTheme.tabsTheme.selectedTextColor(themeMode)
    };
  }
}}

    ${_ref7 => {
  let {
    themeMode
  } = _ref7;
  return (0, _createFocusRingStyles.createFocusRingStyles)({
    theme: themeMode,
    componentRadius: _tabsTheme.tabsTheme.focusRingRadius(themeMode),
    gap: _tabsTheme.tabsTheme.focusRingGap(themeMode),
    componentBorderWidth: 0,
    secondaryWidth: 2
  });
}};

    &:focus::before {
        // Customize the focusRingStyles from above.
        bottom: 0;
        border-bottom-left-radius: ${_ref8 => {
  let {
    themeMode
  } = _ref8;
  return _tabsTheme.tabsTheme.focusRingRadiusOverride(themeMode);
}};
        border-bottom-right-radius: ${_ref9 => {
  let {
    themeMode
  } = _ref9;
  return _tabsTheme.tabsTheme.focusRingRadiusOverride(themeMode);
}};
        // Customize the top padding when focused in Ursa.
        inset-block-start: ${_ref10 => {
  let {
    themeMode
  } = _ref10;
  return _tabsTheme.tabsTheme.focusRingInsetOverride(themeMode);
}};
    }

    &[aria-disabled="true"] {
        color: ${_ref11 => {
  let {
    themeMode
  } = _ref11;
  return _tabsTheme.tabsTheme.disabledTextColor(themeMode);
}};
        cursor: not-allowed;
    }
`);
exports.StyledTab = StyledTab;
StyledTab.displayName = "StyledTab";
const Tab = /*#__PURE__*/(0, _react.forwardRef)((_ref12, parentRef) => {
  let {
    children,
    isDisabled,
    id,
    ...rest
  } = _ref12;
  const {
    key
  } = (0, _useTheme.useTheme)();
  const themeMode = key;
  const {
    disableAutoTabFocusWhenSwitching,
    onlyRenderActiveTabPanelChildren: _0,
    onChange,
    renderOn,
    registerTab,
    selectedId,
    manual,
    focusPreviousTab,
    focusNextTab,
    tabRegistrations
  } = (0, _TabsContext.useTabsContext)();
  const finalId = (0, _react.useMemo)(() => id ?? (0, _lodestarUtils.genId)("sds--tab-id"), [id]);
  const localRef = (0, _react.useRef)(null);
  const ref = (0, _lodestarUtils.useForkRef)(localRef, parentRef);
  const thisReg = tabRegistrations.find(_ref13 => {
    let {
      tabId
    } = _ref13;
    return tabId === finalId;
  });
  (0, _react.useEffect)(() => {
    const {
      unregister
    } = registerTab(finalId, localRef);
    return unregister;
  }, [finalId, localRef, registerTab]);

  // If the selectedId changes and this tab is the newly selected tab, focus it.
  // This method allows the user to control the selected state of the tab from outside.
  const previousSelectedId = (0, _web.usePrevious)(selectedId);
  (0, _react.useEffect)(() => {
    if (disableAutoTabFocusWhenSwitching) {
      return;
    }
    if (
    // Note - this should only be run AFTER the initial render, otherwise it could cause
    // focus issues where a tab is focused twice which introduces oddities with the underline.
    previousSelectedId && previousSelectedId !== selectedId && selectedId === finalId && !manual && localRef.current !== document.activeElement) {
      localRef.current?.focus();
    }
  }, [disableAutoTabFocusWhenSwitching, finalId, previousSelectedId, selectedId, manual, thisReg?.tabRef]);
  const isSelected = finalId === selectedId;
  const onClick = (0, _lodestarUtils.callAllHandlers)(() => {
    if (!isSelected && !isDisabled) {
      onChange(finalId);
    }
  }, rest.onClick);
  const onKeyDown = (0, _lodestarUtils.callAllHandlers)(e => {
    switch (e.key) {
      case "ArrowLeft":
        focusPreviousTab();
        break;
      case "ArrowRight":
        focusNextTab();
        break;
    }
  }, rest.onKeyDown);
  return /*#__PURE__*/_react.default.createElement(StyledTab, (0, _extends2.default)({}, rest, {
    id: finalId,
    ref: ref,
    selected: isSelected,
    themeMode: themeMode,
    onClick: onClick,
    onKeyDown: onKeyDown,
    "aria-disabled": isDisabled,
    "aria-controls": thisReg?.panelId ?? undefined,
    "aria-selected": isSelected,
    renderOn: renderOn,
    tabIndex: isSelected ? 0 : -1,
    role: "tab"
  }), children);
});
exports.Tab = Tab;
Tab.displayName = "Tab";
//# sourceMappingURL=Tab.js.map